import {
  Button,
  InputBase,
  CircularProgress,
  Alert,
  Checkbox,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { setTokensFromResponse } from "../../utils/authTokens";
import { AlertCircle, Building2, LogOut, Trash2 } from "lucide-react";
import DeleteUserAccountModal from "../modals/DeleteUserAccountModal";

const CreateOrganization = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [user, setUser] = useState({
    organization_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    userId: JSON.parse(localStorage.getItem("user"))?.userId?.S || null,
    email: JSON.parse(localStorage.getItem("user"))?.email?.S || null,
    password: JSON.parse(localStorage.getItem("user"))?.password?.S || null,
    first_name: JSON.parse(localStorage.getItem("user"))?.fName?.S || null,
    last_name: JSON.parse(localStorage.getItem("user"))?.lName?.S || null,
    company_name:
      JSON.parse(localStorage.getItem("organization"))?.organization_name?.S ||
      "",
    agreed: false,
    registeredFromGoogle:
      JSON.parse(localStorage.getItem("user"))?.registeredFromGoogle?.BOOL ||
      false,
  });

  const addOrganization = async () => {
    setErrorMsg(null);
    if (!user.agreed) {
      setErrorMsg("Please agree to the Terms of Service and Privacy Policy.");
      return;
    }
    if (user.company_name.length >= 1) {
      setLoader(true);
      await axios
        .post(
          process.env.REACT_APP_BACKEND_URL + "user/add-created-user-to-org",
          {
            ...user,
            userId: JSON.parse(localStorage.getItem("user"))?.userId?.S,
          }
        )
        .then((res) => {
          if (res.data.status === "success") {
            localStorage.setItem("user", JSON.stringify(res.data.user));
            localStorage.setItem(
              "organization",
              JSON.stringify(res.data.organization)
            );
            setTokensFromResponse(res.data);
            setSuccessMsg(
              "Organization has been created, we would now redirect you to complete onboarding flow. "
            );
            setTimeout(() => {
              navigate("/register");
              setLoader(false);
            }, 4000);
          } else {
            setErrorMsg(`${res.data.msg}`);
            setLoader(false);
          }
        })
        .catch(() => {
          setLoader(false);
        });
    } else {
      setErrorMsg("Please enter company name for your organization");
    }
  };

  const handleLogout = () => {
    console.info(
      "[Auth] Logging out: user chose Sign out from create-organization page."
    );
    localStorage.clear();
    navigate("/login");
  };

  const deleteUser = async () => {
    setErrorMsg(null);
    setLoader(true);
    await axios.post(
      process.env.REACT_APP_BACKEND_URL + "delete-user-by-id",
      {
        userId: JSON.parse(localStorage.getItem("user")).userId.S,
      }
    );
    setSuccessMsg("User deleted successfully");
    localStorage.clear();
    setTimeout(() => {
      navigate("/login");
      setLoader(false);
    }, 2000);
  };

  const [openDeleteAccountModal, setOpenDeleteAccountModal] = useState(false);

  useEffect(() => {
    if (
      JSON.parse(localStorage.getItem("user")) &&
      JSON.parse(localStorage.getItem("organization"))
    ) {
      console.info("[Auth] Redirecting to dashboard: user already has organization in storage.");
      navigate("/dashboard");
    }
    if (!JSON.parse(localStorage.getItem("user"))) {
      console.info("[Auth] Redirecting to login: no user in storage (create-organization page).");
      navigate("/login");
    }
  }, []);

  return (
    <div>
      <DeleteUserAccountModal
        loader={loader}
        setLoader={setLoader}
        deleteUser={deleteUser}
        open={openDeleteAccountModal}
        setOpen={setOpenDeleteAccountModal}
      />
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <AlertCircle className="h-10 w-10 text-red-500" aria-hidden />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Oops! You are not a member of any organizations.
          </h2>
          <p className="mt-2 text-center text-base text-gray-600">
            If you would like to create one, provide your company name below.
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-xl sm:px-10">
            {errorMsg && (
              <div className="rounded-md bg-red-50 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Create organization failed
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{errorMsg}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {successMsg && (
              <Alert color="success" sx={{ mb: 2 }}>
                <div className="text-[14px]">{successMsg}</div>
              </Alert>
            )}

            <div className="space-y-6">
              <div>
                <label
                  htmlFor="company_name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Company Name
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building2 className="h-5 w-5 text-gray-400" />
                  </div>
                  <InputBase
                    id="company_name"
                    value={user.company_name}
                    onChange={(e) => {
                      setUser({ ...user, company_name: e.target.value });
                      setErrorMsg(null);
                    }}
                    disabled={loader}
                    placeholder="Acme Corp"
                    sx={{
                      bgcolor: "white",
                      border: "1px solid #D1D5DB",
                      width: "100%",
                      pl: "40px",
                      pr: "12px",
                      py: "8px",
                      borderRadius: "6px",
                      fontSize: "14px",
                      "&:focus": {
                        outline: "none",
                        ring: "2px",
                        ringColor: "#605BFF",
                        borderColor: "#605BFF",
                      },
                      "&:disabled": {
                        bgcolor: "#F9FAFB",
                        color: "#6B7280",
                      },
                    }}
                  />
                </div>
              </div>

              <div className="flex items-start">
                <Checkbox
                  checked={user.agreed}
                  onChange={(e) =>
                    setUser({ ...user, agreed: e.target.checked })
                  }
                  disabled={loader}
                  sx={{
                    color: "#605BFF",
                    "&.Mui-checked": {
                      color: "#605BFF",
                    },
                    pt: 0,
                    pb: 0,
                  }}
                />
                <label className="ml-2 block text-sm text-gray-700 pt-2.5 cursor-pointer">
                  I agree to the{" "}
                  <a
                    href="https://twilanswer.com/terms-and-conditions/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-brand-primary hover:text-brand-hover underline"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://twilanswer.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-brand-primary hover:text-brand-hover underline"
                  >
                    Privacy Policy
                  </a>
                  .
                </label>
              </div>

              <Button
                fullWidth
                variant="contained"
                color="primary"
                disabled={loader || !user.company_name?.trim() || !user.agreed}
                onClick={() => addOrganization()}
                sx={{
                  py: "12px",
                  px: "16px",
                  borderRadius: "6px",
                  fontSize: "16px",
                  "&:disabled": {
                    opacity: 0.5,
                    cursor: "not-allowed",
                  },
                }}
              >
                {loader ? (
                  <CircularProgress size="1rem" sx={{ color: "white" }} />
                ) : (
                  "Create Organization"
                )}
              </Button>

              <div className="mt-6 pt-6 border-t border-gray-200 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() => {
                    const a = document.createElement("a");
                    a.href = "mailto:hello@twilanswer.com";
                    a.click();
                  }}
                  className="text-sm text-gray-700 hover:text-gray-900 underline font-medium text-left sm:text-center"
                >
                  Contact Support
                </button>
                <div className="flex flex-wrap items-center gap-4 sm:justify-end">
                  <button
                    type="button"
                    disabled={loader}
                    onClick={handleLogout}
                    className="flex items-center gap-1.5 text-sm text-gray-700 hover:text-gray-900 underline font-medium disabled:opacity-50"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                  <button
                    type="button"
                    disabled={loader}
                    onClick={() => setOpenDeleteAccountModal(true)}
                    className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 underline font-medium disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization;
